import { isTuningCommand, isTuningExperimentRequest } from '@playcastdotio/PeerCore';
import type { TuningLocalEvent } from '@playcastdotio/PeerCore';
import type { HostTuningPreloadBridge, HostTuningRpcRequest, IHostTuningControl } from '../../coordinators/streamTuning/HostTuningContracts';

/** Closed main-to-renderer RPC dispatcher; no eval or arbitrary method/channel access. */
export class HostTuningIpcService {
  private control?: IHostTuningControl;
  private unsubscribe?: () => void;
  private disposed = false;
  constructor(private readonly bridge: HostTuningPreloadBridge | undefined) {}
  public bind(control: IHostTuningControl): void {
    this.control = control; this.unsubscribe?.();
    this.unsubscribe = this.bridge?.onRequest(request => { void this.respond(request); });
  }
  public publish(event: TuningLocalEvent): void { if (!this.disposed) this.bridge?.publishEvents([event]); }
  public arm(sessionId: string, ttlMs: number): Promise<unknown> {
    if (!this.bridge || this.disposed) return Promise.reject(new Error('preload-unavailable'));
    return this.bridge.arm(sessionId, ttlMs);
  }
  public dispose(): void { this.disposed = true; this.unsubscribe?.(); this.control = undefined; }
  private async respond(request: HostTuningRpcRequest): Promise<void> {
    if (this.disposed || !this.bridge) return;
    try {
      const value = await this.dispatch(request);
      if (!this.disposed) this.bridge.respondRequest({ id: request.id, ok: true, value });
    } catch (error) {
      if (!this.disposed) this.bridge.respondRequest({ id: request.id, ok: false,
        error: { code: (error as Error).message.slice(0, 128), message: (error as Error).message.slice(0, 2048) } });
    }
  }
  private dispatch(request: HostTuningRpcRequest): unknown | Promise<unknown> {
    const c = this.control; const a = request.args;
    if (!c || typeof request.id !== 'string' || request.id.length > 128 || !Array.isArray(a)) throw new Error('invalid-rpc');
    const str = (index: number): string => {
      if (typeof a[index] !== 'string' || (a[index] as string).length > 128) throw new Error('invalid-rpc-argument');
      return a[index] as string;
    };
    const num = (index: number): number => {
      if (typeof a[index] !== 'number' || !Number.isFinite(a[index])) throw new Error('invalid-rpc-argument');
      return a[index] as number;
    };
    switch (request.method) {
      case 'capabilities': return c.capabilities(a[0] === undefined ? undefined : str(0));
      case 'sessions': return c.sessions();
      case 'session': return c.session(str(0));
      case 'trial': return c.trial(str(0));
      case 'events': return c.events(str(0), num(1), num(2), a[3] === undefined ? undefined : str(3));
      case 'arm': return c.arm(str(0), num(1));
      case 'execute': if (!isTuningCommand(a[1])) throw new Error('invalid-command'); return c.execute(str(0), a[1]);
      case 'startExperiment': if (!isTuningExperimentRequest(a[0])) throw new Error('invalid-experiment'); return c.startExperiment(a[0]);
      case 'experiment': return c.experiment(str(0));
      case 'cancelExperiment': {
        const value = (a[2] ?? a[1]) as { requestId?: unknown; sessionId?: unknown; leaseId?: unknown; reason?: unknown } | undefined;
        if (!value || typeof value.sessionId !== 'string' || typeof value.leaseId !== 'string'
          || typeof value.reason !== 'string' || value.reason.length > 128
          || (a[2] !== undefined && str(1) !== value.reason)) throw new Error('invalid-cancel');
        return this.cancel(c, str(0), value as { sessionId: string; leaseId: string; reason: string });
      }
      case 'revoke': return c.revoke(str(0), str(1));
      default: throw new Error('unsupported-rpc');
    }
  }
  private async cancel(control: IHostTuningControl, id: string, request: { sessionId: string; leaseId: string; reason: string }) {
    const snapshot = await control.experiment(id);
    const authority = control as IHostTuningControl & { hasLease?(sessionId: string, leaseId: string): boolean };
    if (!snapshot || snapshot.sessionId !== request.sessionId || !authority.hasLease?.(request.sessionId, request.leaseId)) {
      throw new Error('experiment-unauthorized');
    }
    return control.cancelExperiment(id, request.reason);
  }
}
